import React from 'react';
import { ArticleLayout } from '@/components/ArticleLayout';
import { useLanguage } from '@/contexts/LanguageContext';

export const TransfoDroit2025Page = () => {
  const { language } = useLanguage();

  const content = language === 'fr' ? `
## Sommaire

- [TransfoDroit 2025 : le rendez-vous incontournable de l'IA juridique](#transfo-droit-2025)
- [Jour 1 — 25 novembre : l'IA au cœur de la stratégie](#jour-1)
- [Conférence d'ouverture : la stratégie IA du ministère de la Justice](#conference-ouverture)
- [Anatomie d'une IA juridique par Ordalie](#anatomie-ia)
- [Harvey, Jimini AI, Doctrine, LexisNexis : les stars du salon](#outils-ia)
- [Microsoft et les agents IA : le parcours dédié](#microsoft-agents)
- [Le juriste augmenté et la formation IA](#juriste-augmente)
- [Jour 2 — 26 novembre : souveraineté et prospective](#jour-2)
- [Session CNB : l'étude « Les Avocats et l'IA »](#session-cnb)
- [Conférence de clôture : le Legal Data Space](#legal-data-space)
- [Révolution IA : quelle évolution à 1, 3, 5 et 10 ans ?](#prospective)
- [Les chiffres clés de l'édition 2025](#chiffres-cles)
- [Les replays disponibles](#replays)
- [FAQ](#faq)

---

## TransfoDroit 2025 : le rendez-vous incontournable de l'IA juridique {#transfo-droit-2025}

Les **25 et 26 novembre 2025**, la [Cité des Sciences et de l'Industrie de La Villette](https://www.cite-sciences.fr/) à Paris a accueilli la **9ᵉ édition des Rendez-vous des Transformations du Droit** (#TransfoDroit), organisée par le [Village de la Justice](https://www.village-justice.com/) et [Open Law*](https://openlaw.fr/). Avec **plus de 4 800 participants**, **80 exposants** et plus de **120 conférences et ateliers**, cette édition a confirmé son statut de **plus grand congrès européen** dédié à l'innovation dans les professions du droit.

Le thème de cette année : **« L'Humain à la main »**, un parti pris assumé pour rappeler que la transformation numérique du droit — et l'IA juridique en particulier — ne se conçoit pas sans l'intelligence, le discernement et l'éthique des professionnels.

> « Est-ce que l'on peut parler en 2025 d'un écosystème qui se structure et gagne en lisibilité ? Oui. Est-ce que nous avons collectivement un rôle à jouer pour que ce nouvel état de fait façonné par la technologie soit conforme à nos valeurs ? La réponse est définitivement oui. » — **Sumi Saint Auguste**, Présidente d'Open Law*, en ouverture du congrès.

*[Source : Village de la Justice, « Congrès Transfodroit 2025 : ce qu'on y a vu, entendu et aimé », 28 novembre 2025.](https://www.village-justice.com/articles/transfodroit-2024-entendu-aime,51595.html)*

---

## Jour 1 — 25 novembre : l'IA au cœur de la stratégie {#jour-1}

La première journée a été dense : dès 9h30, les amphithéâtres et salles d'ateliers se remplissaient, malgré une grève partielle des agents de sécurité qui a rallongé l'entrée des premiers visiteurs.

### Conférence d'ouverture : la stratégie IA du ministère de la Justice {#conference-ouverture}

La conférence d'ouverture a posé le cadre institutionnel. **Alexandre de Bosschère**, Secrétaire général adjoint au ministère de la Justice, et **Élise Farge Di Maria**, Cheffe de projet IA, ont présenté les **conclusions du rapport IA et Justice** remis en juin 2025 au Garde des Sceaux Gérald Darmanin.

Ce rapport, réalisé sous la direction de **Haffide Boulakras** (directeur adjoint de l'École nationale de la magistrature), formule des préconisations concrètes pour l'intégration de l'IA dans le fonctionnement de la justice. Les intervenants ont dévoilé les **premières étapes de concrétisation** : projets pilotes dans les juridictions, outils d'aide à la décision, et incubateur du ministère avec les start-ups d'État **JAFER** et **Mon indemnisation Justice**.

*[Source : programme officiel TransfoDroit 2025](https://www.transformations-droit.com/conference-d-ouverture-strategie-ia-au-sein-de-la-justice-des-preconisations) — [Compte-rendu disponible sur le Village de la Justice](https://www.village-justice.com/articles/replay-service-justice,55352.html)*

---

### Anatomie d'une IA juridique par Ordalie {#anatomie-ia}

L'une des conférences les plus pédagogiques du salon a été proposée par **[Ordalie](https://www.ordalie.com/)**, avec sa CEO **Léa Fleury** et son CTO **Baudouin Arbarétier**. En 45 minutes, ce dernier a littéralement « dessiné en direct » les entrailles de la technologie derrière l'IA juridique, en abordant sans langue de bois :

- **Les hallucinations** : jusqu'où se fier aux réponses de l'IA et comment en garantir la fiabilité ?
- **IA générative vs analytique** : que fait chaque type d'IA et dans quels cas les utiliser ?
- **L'entraînement des modèles** : avec quelles données et quel contrôle qualité ?
- **Confidentialité et sécurité des données** : que deviennent vos données ?
- **Le prompting** : faut-il vraiment le maîtriser pour utiliser efficacement une IA juridique ?

Le public est reparti avec une **grille simple pour prioriser les usages**, verrouiller les process et aligner les équipes.

*[Source : programme officiel TransfoDroit](https://www.transformations-droit.com/ordalie-anatomie-d-une-ia-juridique)*

---

### Harvey, Jimini AI, Doctrine, LexisNexis : les stars du salon {#outils-ia}

L'édition 2025 a été marquée par la **présence massive des acteurs de l'IA juridique**, chacun présentant ses dernières innovations :

**[Doctrine](https://www.doctrine.fr/)** a proposé deux sessions dédiées : « L'IA juridique pour analyser, rechercher, rédiger » et « L'IA juridique pour les directions d'entreprises », démontrant l'étendue de ses fonctionnalités d'IA appliquées au droit français.

**[Jimini AI](https://www.jimini-ai.com/)**, fort de son [partenariat récent avec Gide Loyrette Nouel](/articles/jimini-ai-gide-partenariat), a présenté en session « De la théorie à la pratique : comment les professionnels du droit utilisent Jimini au quotidien ».

**Harvey**, le géant américain de l'IA juridique, était représenté via **DayTwo** qui a proposé la session « Innovation IA en Direction juridique : l'intégration d'Harvey, témoignages ».

**[LexisNexis](https://www.lexisnexis.fr/)** a déroulé un programme complet avec « Dompter l'IA : de la promesse à la performance juridique sécurisée », « L'art du closing » et « IA : Comment organiser la confiance pour créer de la valeur ».

**[Lefebvre Dalloz](https://www.lefebvre-dalloz.fr/)** a multiplié les ateliers de démonstration de son IA **GenIA-L** — pour le secteur public, les directions juridiques et les avocats — confirmant la montée en puissance de cet outil que nous avons [analysé en détail](/articles/genial-dalloz-formation-ia-avocat-analyse).

**[Tomorro](https://www.tomorro.com/)** a animé pas moins de 8 sessions sur les deux jours, avec un focus sur la gestion contractuelle et le CLM augmenté par l'IA, incluant des retours d'expérience de **Nestlé**, **Sodebo** et **WWF**.

**[Autolex](https://www.autolex.ai/)**, l'un des newcomers remarqués, a présenté « De copilote à autopilote, la première IA juridique 100% autonome ».

---

### Microsoft et les agents IA : le parcours dédié {#microsoft-agents}

**[Microsoft](https://www.microsoft.com/)** a été un partenaire majeur de cette édition avec un programme dédié s'étalant sur les deux jours. Le parcours couvrait :

- **La gouvernance de l'IA** : quels défis à relever ?
- **Les systèmes agentiques** : comment les intégrer sans perdre le contrôle ?
- **Coaching Copilot** : démonstrations en direct
- **Création d'agents IA** : contexte, définitions, cas d'usage et workflows
- **Inside Microsoft** : gestion des réunions et des documents avec Copilot

Le mot clé **« agents IA »** était sur toutes les lèvres. Comme le résume le [Village de la Justice](https://www.village-justice.com/) dans son compte-rendu, c'est « la prochaine étape de développement de l'IA » qui s'est clairement imposée comme le fil rouge de cette édition.

---

### Le juriste augmenté et la formation IA {#juriste-augmente}

Plusieurs sessions étaient consacrées à la **montée en compétences des professionnels du droit** :

- **[Gino LegalTech](https://www.ginolegaltech.com/)** proposait un atelier pratique « Devenez un juriste augmenté grâce à l'IA » (places limitées à 20 participants, toutes pourvues).
- **Side Quest Formation** animait « Passer à l'IA en 2026 : méthodologie, facteurs clés de succès et cas d'usages » et « Le prompt est mort, vive le prompt ! ».
- La conférence **« IA et formation : votre expertise a plus de valeur que vous ne le pensez »** rappelait que la formation n'est pas un luxe mais une nécessité stratégique.

L'AFJE (Association Française des Juristes d'Entreprise) a quant à elle proposé un « B.A.-BA de la mise en place d'un projet IA en Direction juridique » et une session « DJ : où en êtes-vous de votre transformation ? ».

---

## Jour 2 — 26 novembre : souveraineté et prospective {#jour-2}

La deuxième journée a marqué un tournant plus stratégique et institutionnel.

### Session CNB : l'étude « Les Avocats et l'IA » {#session-cnb}

Le **[Conseil National des Barreaux (CNB)](https://www.cnb.avocat.fr/)** a présenté les résultats de son **enquête d'envergure sur l'intelligence artificielle et la profession d'avocat**. Animée par **François Girault** (Commission Prospective et Innovation du CNB), avec les interventions de **Coline Alméras-Vaillant** (Directrice Technologies chez PwC) et **Sophie de Chanaud** (Directrice juridique corporate d'AXA, Présidente du Comité Droit des sociétés du MEDEF), cette session a dressé :

- Une **cartographie des usages** de l'IA par les avocats
- Une évaluation des **besoins et attentes** de la profession
- L'enjeu crucial de la **formation des équipes**
- L'**impact de l'IA sur l'organisation des structures** et la **relation client**

Le CNB a également tenu une session sur la **souveraineté culturelle et la défense du droit continental**, avec la Présidente **Julie Couturier**, et une session sur le **financement de l'innovation et le soutien aux avocats**.

*[Source : compte-rendu et replay audio sur le Village de la Justice](https://www.village-justice.com/articles/conference-intelligence-artificielle-les-avocats,55562.html)*

---

### Conférence de clôture : le Legal Data Space {#legal-data-space}

La conférence de clôture, coordonnée par **Martin Bussy** et **Thomas Saint-Aubin** (Legal Data Space) et animée par **Dan Kohn** (Septeo) et **Emilie Viard** (Juriconnexion), a été un moment fort de cette édition. Le projet **Legal Data Space**, lancé en mars 2025, rassemble les acteurs du droit pour construire une **IA juridique souveraine** grâce à des biens communs numériques partagés.

Les premiers livrables présentés (format pitch) couvraient des sujets majeurs :

- **Compliance by design** : le standard **ELI-LO**, une ontologie européenne pour modéliser les obligations de conformité (projet COMPLIANCE-X)
- **LEGAL RULEBOOK** : traduction des règles juridiques en standards techniques selon la démarche « Law is Code », avec un cas pratique RGPD et Data Act
- **MYLEGALCONNECT** : une identité numérique souveraine interopérable avec EU Wallet et France Connect
- **LEGAL MCP** : le serveur **OpenLegi**, un MCP interopérable pour les agents IA interrogeant les ressources juridiques ouvertes
- **LEGAL RAG** : charte éditoriale et modèles de prompts pour fiabiliser les agents IA thématiques
- **LEGAL RAGTIME** : évaluation de la fiabilité des assistants juridiques IA (solution open source)
- **LEGAL LLM** : travaux de finetuning de **Mistral** pour l'adapter au droit romano-civiliste, avec **Jimini AI** (lauréat BPI)

La conférence s'est conclue par le **lancement de la première bibliothèque d'agents IA juridiques** (contrats, corporate, M&A, compliance) et l'ouverture de la **marketplace du Legal Data Space**, en présence de **François Girault** (CNB).

*[Source : programme officiel TransfoDroit](https://www.transformations-droit.com/conference-de-cloture-legal-data-space-quels-biens-communs-numeriques-pour-la)*

---

### Révolution IA : quelle évolution à 1, 3, 5 et 10 ans ? {#prospective}

La conférence de **Mathieu Bouillon** (Flits, agence de conseil legal IA) a été l'une des plus marquantes du salon. Dans un exercice de prospective qui a « mis le feu aux cerveaux des juristes présents », il a détaillé :

- **À 1 an** : premiers **écarts de productivité mesurables** entre les juristes « augmentés » et les autres
- **À 3 ans** : explosion des **agents juridiques autonomes**, IA comme mémoire vivante du cabinet
- **À 5 ans** : l'IA devient le **« new normal »**, tâches auto-exécutées
- **À 10 ans** : IA comme **assistant numérique personnel** de chaque juriste, métiers pensés autour de l'IA

Quelques punchlines restées dans les mémoires :

> « L'IA n'est plus une tendance, c'est un séisme silencieux qui redessine déjà le monde du droit. »

> « L'IA va mettre le dernier clou au cercueil de la facturation à l'heure. »

> « Dans 5 ans, l'IA... c'est Word maintenant. »

> « Si tu ne veux pas être remplacé par un robot... arrête de te comporter comme un robot. »

*[Source : replay disponible sur le Village de la Justice](https://www.village-justice.com/articles/revolution-que-doit-attendre-evolution-des-professionnels-droit-ans,55460.html)*

---

## Les chiffres clés de l'édition 2025 {#chiffres-cles}

| Indicateur | Chiffre |
|---|---|
| Participants | **4 800+** |
| Exposants | **80** |
| Conférences et ateliers | **120+** |
| Jours | **2** (25-26 novembre) |
| Lieu | Cité des Sciences, La Villette, Paris |
| Édition | **9ᵉ** |
| Prochaine édition | **24-25 novembre 2026** |

*[Source : Village de la Justice](https://www.village-justice.com/articles/transfodroit-2024-entendu-aime,51595.html)*

---

## Les replays disponibles {#replays}

Plusieurs conférences majeures sont disponibles en replay sur le [Village de la Justice](https://www.village-justice.com/) :

- [**Stratégie IA au sein de la justice**](https://www.village-justice.com/articles/replay-service-justice,55352.html) — Conférence d'ouverture avec le ministère de la Justice
- [**Révolution IA : évolution des professionnels du droit à 1, 3, 5 et 10 ans**](https://www.village-justice.com/articles/revolution-que-doit-attendre-evolution-des-professionnels-droit-ans,55460.html) — Mathieu Bouillon
- [**Les juristes d'entreprise à l'ère de l'IA**](https://www.village-justice.com/articles/replay-transfodroit-les-juristes-entreprise-ere-gestion-risque-strategie,55866.html) — De la gestion du risque à la stratégie d'affaires
- [**De l'IA générative à l'IA agentique**](https://www.village-justice.com/articles/replay-transfodroit-generative-agentique-nouvelle-ere-des-directions-juridiques,55673.html) — La nouvelle ère des Directions Juridiques
- [**Comment sensibiliser les équipes à la bonne utilisation de l'IA**](https://www.village-justice.com/articles/replay-transfodroit-comment-sensibiliser-les-equipes-entreprise-bonne,55869.html) — Open Law*
- [**L'intelligence artificielle et les avocats : l'enquête du CNB**](https://www.village-justice.com/articles/conference-intelligence-artificielle-les-avocats,55562.html) — Replay audio
- [**TransfoTax 2025 : les fiscalistes aussi se transforment**](https://www.village-justice.com/articles/conference-transfotax-2025-les-fiscalistes-aussi-transforment,55568.html)

Retrouvez l'ensemble du programme sur le **[site officiel des Rendez-vous des Transformations du Droit](https://www.transformations-droit.com/)**.

---

## Les mots-clés de TransfoDroit 2025

Le Village de la Justice a identifié les mots et concepts qui ont rythmé les deux journées :

- **« Agents IA »** — La prochaine étape de développement de l'IA
- **« Accompagnement »** — Et non plus seulement « acculturation »
- **« Legal Ops »** — La fonction qui monte et dont on comprend l'importance
- **« Souveraineté »** — Technologique, culturelle et juridique
- **« Quick win »** — L'approche pragmatique pour démarrer sa transformation
- **« Human in the loop »** — L'humain dans la boucle, thème central
- **« Cas d'usage »** — Les professionnels se sont mis à les définir concrètement

---

## Ce que Gaius retient de TransfoDroit 2025

L'édition 2025 marque un **tournant de maturité** pour l'écosystème de l'IA juridique en France. Trois enseignements clés :

**1. L'IA juridique n'est plus un sujet de curiosité, c'est un enjeu stratégique.** Avec les démonstrations d'Harvey, Jimini AI, Doctrine, Ordalie, GenIA-L et tant d'autres, la question n'est plus « faut-il adopter l'IA ? » mais « comment l'adopter efficacement et en toute sécurité ? ».

**2. La formation est plus que jamais centrale.** Que ce soit via les sessions de Gino LegalTech, Side Quest Formation, ou notre propre [offre de formation IA juridique](/formations), le constat est unanime : sans montée en compétences, pas de transformation réussie.

**3. La souveraineté juridique devient un impératif.** Le Legal Data Space, le finetuning de Mistral par Jimini AI, le standard ELI-LO : la profession se dote d'outils souverains pour ne pas dépendre exclusivement des modèles anglo-saxons.

---

## FAQ {#faq}

### Qu'est-ce que TransfoDroit ?

**TransfoDroit** (ou « Les Rendez-vous des Transformations du Droit ») est le **plus grand salon européen** dédié à l'innovation dans les professions du droit. Organisé chaque année par le [Village de la Justice](https://www.village-justice.com/) et [Open Law*](https://openlaw.fr/), il rassemble avocats, juristes, directions juridiques, étudiants et acteurs de la legal tech autour de conférences, ateliers et expositions.

### Quand aura lieu la prochaine édition ?

La **10ᵉ édition** des Rendez-vous des Transformations du Droit se tiendra les **24 et 25 novembre 2026** à la Cité des Sciences de La Villette, Paris. [Pré-inscription disponible sur le site officiel](https://www.transformations-droit.com/).

### Quels outils d'IA juridique étaient présentés à TransfoDroit 2025 ?

Les principaux outils d'IA juridique présentés étaient : **Doctrine**, **Jimini AI**, **Harvey**, **LexisNexis / Lexis+ AI**, **Lefebvre Dalloz / GenIA-L**, **Ordalie**, **Tomorro**, **Autolex**, **Luminance**, **Legix**, **Juri'Predis**, **Legora**, **SingleCase**, **Microsoft Copilot** et **ucontrolAI**.

### Comment se former à l'IA juridique après TransfoDroit ?

[Gaius](/) propose des **[formations IA juridique](/formations)** adaptées aux avocats, juristes d'entreprise et directions juridiques. Nos programmes couvrent les outils présentés à TransfoDroit (ChatGPT, Claude, Doctrine, Jimini AI, Harvey) avec une approche pragmatique et des cas d'usage concrets. [Contactez-nous](/contact) pour un programme sur mesure.
` : `
## Summary

- [TransfoDroit 2025: The Essential Legal AI Event](#transfo-droit-2025)
- [Day 1 — November 25: AI at the Heart of Strategy](#day-1)
- [Opening Conference: Ministry of Justice AI Strategy](#opening-conference)
- [Key AI Tools Showcased](#ai-tools)
- [Day 2 — November 26: Sovereignty and Foresight](#day-2)
- [CNB Session: "Lawyers and AI" Study](#cnb-session)
- [Closing Conference: Legal Data Space](#legal-data-space)
- [Key Takeaways](#takeaways)
- [Available Replays](#replays)
- [FAQ](#faq)

---

## TransfoDroit 2025: The Essential Legal AI Event {#transfo-droit-2025}

On **November 25-26, 2025**, the Cité des Sciences et de l'Industrie in Paris hosted the **9th edition of the Rendez-vous des Transformations du Droit** (#TransfoDroit), organized by [Village de la Justice](https://www.village-justice.com/) and [Open Law*](https://openlaw.fr/). With **over 4,800 attendees**, **80 exhibitors**, and more than **120 conferences and workshops**, this edition confirmed its status as **Europe's largest congress** dedicated to legal innovation.

The theme: **"Human in Command"** — emphasizing that legal AI must serve, not replace, professional judgment.

---

## Day 1 — November 25: AI at the Heart of Strategy {#day-1}

### Opening Conference: Ministry of Justice AI Strategy {#opening-conference}

**Alexandre de Bosschère** (Deputy Secretary General, Ministry of Justice) and **Élise Farge Di Maria** (AI Project Manager) presented the conclusions of the **AI and Justice report** submitted to the Minister in June 2025, and revealed the first implementation steps.

### Key AI Tools Showcased {#ai-tools}

Major legal AI players were present: **[Doctrine](https://www.doctrine.fr/)**, **[Jimini AI](https://www.jimini-ai.com/)**, **Harvey** (via DayTwo), **[LexisNexis](https://www.lexisnexis.fr/)**, **[Lefebvre Dalloz](https://www.lefebvre-dalloz.fr/)** with GenIA-L, **[Tomorro](https://www.tomorro.com/)**, **[Ordalie](https://www.ordalie.com/)**, **[Autolex](https://www.autolex.ai/)**, **[Microsoft](https://www.microsoft.com/)** with Copilot, and many more.

---

## Day 2 — November 26: Sovereignty and Foresight {#day-2}

### CNB Session: "Lawyers and AI" Study {#cnb-session}

The **French National Bar Council (CNB)** presented its major survey on AI and the legal profession, with PwC and AXA representatives, covering usage mapping, training needs, and the impact on client relationships.

### Closing Conference: Legal Data Space {#legal-data-space}

The closing session presented the **Legal Data Space** project: sovereign legal AI tools, European ontologies (ELI-LO), Mistral fine-tuning for civil law, and the launch of the **first library of legal AI agents**.

---

## Key Takeaways {#takeaways}

| Metric | Figure |
|---|---|
| Attendees | **4,800+** |
| Exhibitors | **80** |
| Conferences & Workshops | **120+** |
| Days | **2** (Nov 25-26) |
| Next Edition | **Nov 24-25, 2026** |

---

## Available Replays {#replays}

Several major conferences are available as replays on [Village de la Justice](https://www.village-justice.com/):

- [**AI Strategy in the Justice System**](https://www.village-justice.com/articles/replay-service-justice,55352.html)
- [**AI Revolution: Legal Professionals in 1, 3, 5 and 10 years**](https://www.village-justice.com/articles/revolution-que-doit-attendre-evolution-des-professionnels-droit-ans,55460.html)
- [**In-House Counsel in the AI Era**](https://www.village-justice.com/articles/replay-transfodroit-les-juristes-entreprise-ere-gestion-risque-strategie,55866.html)
- [**From Generative to Agentic AI**](https://www.village-justice.com/articles/replay-transfodroit-generative-agentique-nouvelle-ere-des-directions-juridiques,55673.html)

Visit the **[official TransfoDroit website](https://www.transformations-droit.com/)** for the full program.

---

## FAQ {#faq}

### What is TransfoDroit?

TransfoDroit is **Europe's largest congress** dedicated to legal innovation, held annually in Paris by Village de la Justice and Open Law*.

### When is the next edition?

The **10th edition** will take place on **November 24-25, 2026** at the Cité des Sciences, Paris. [Pre-register on the official website](https://www.transformations-droit.com/).

### How to train in legal AI after TransfoDroit?

[Gaius](/) offers [legal AI training programs](/training) covering all major tools showcased at TransfoDroit. [Contact us](/contact) for a tailored program.
`;

  const structuredData = [
    {
      "@context": "https://schema.org",
      "@type": "Article",
      "headline": language === 'fr'
        ? "TransfoDroit 2025 : récapitulatif complet du salon de l'IA juridique"
        : "TransfoDroit 2025: Complete Recap of the Legal AI Congress",
      "description": language === 'fr'
        ? "Récapitulatif sourcé des journées du salon Transfo Droit 2025 : conférences, interventions, innovations IA juridique, replays vidéo et enseignements clés."
        : "Comprehensive recap of TransfoDroit 2025: conferences, speakers, legal AI innovations, video replays and key takeaways.",
      "datePublished": "2025-12-02",
      "dateModified": "2025-12-02",
      "wordCount": language === 'fr' ? 3200 : 1200,
      "author": {
        "@type": "Organization",
        "name": "Gaius Legal AI",
        "url": "https://gaius-tech.com"
      },
      "publisher": {
        "@type": "Organization",
        "name": "Gaius Legal AI",
        "url": "https://gaius-tech.com",
        "logo": { "@type": "ImageObject", "url": "https://gaius-tech.com/gaius-logo.webp" }
      },
      "mainEntityOfPage": {
        "@type": "WebPage",
        "@id": language === 'fr'
          ? "https://gaius-tech.com/articles/transfo-droit-2025-recapitulatif-salon-ia-juridique"
          : "https://gaius-tech.com/articles/transfo-droit-2025-legal-ai-congress-recap"
      },
      "about": [
        { "@type": "Thing", "name": "TransfoDroit" },
        { "@type": "Thing", "name": "IA juridique" },
        { "@type": "Thing", "name": "Legal AI" },
        { "@type": "Thing", "name": "Transformations du droit" }
      ],
      "citation": [
        {
          "@type": "WebPage",
          "name": "Congrès Transfodroit 2025 : ce qu'on y a vu, entendu et aimé",
          "url": "https://www.village-justice.com/articles/transfodroit-2024-entendu-aime,51595.html"
        },
        {
          "@type": "WebPage",
          "name": "Programme officiel TransfoDroit 2025",
          "url": "https://www.transformations-droit.com/plan"
        }
      ]
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": language === 'fr' ? "Qu'est-ce que TransfoDroit ?" : "What is TransfoDroit?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": language === 'fr'
              ? "TransfoDroit (Les Rendez-vous des Transformations du Droit) est le plus grand salon européen dédié à l'innovation dans les professions du droit, organisé par le Village de la Justice et Open Law."
              : "TransfoDroit is Europe's largest congress dedicated to legal innovation, organized annually by Village de la Justice and Open Law."
          }
        },
        {
          "@type": "Question",
          "name": language === 'fr' ? "Quand aura lieu TransfoDroit 2026 ?" : "When is TransfoDroit 2026?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": language === 'fr'
              ? "La 10ᵉ édition se tiendra les 24 et 25 novembre 2026 à la Cité des Sciences de La Villette, Paris."
              : "The 10th edition will take place on November 24-25, 2026 at the Cité des Sciences, Paris."
          }
        },
        {
          "@type": "Question",
          "name": language === 'fr' ? "Quels outils d'IA juridique étaient présentés à TransfoDroit 2025 ?" : "Which legal AI tools were showcased at TransfoDroit 2025?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": language === 'fr'
              ? "Doctrine, Jimini AI, Harvey, LexisNexis, Lefebvre Dalloz / GenIA-L, Ordalie, Tomorro, Autolex, Luminance, Legix, Microsoft Copilot et de nombreux autres acteurs de l'IA juridique."
              : "Doctrine, Jimini AI, Harvey, LexisNexis, Lefebvre Dalloz / GenIA-L, Ordalie, Tomorro, Autolex, Luminance, Legix, Microsoft Copilot and many more legal AI players."
          }
        }
      ]
    }
  ];

  return (
    <ArticleLayout
      seoTitle={language === 'fr'
        ? "TransfoDroit 2025 : récapitulatif du salon IA juridique | Gaius"
        : "TransfoDroit 2025: Legal AI Congress Recap | Gaius"}
      seoDescription={language === 'fr'
        ? "Récapitulatif complet du salon Transfo Droit 2025 : conférences IA juridique, interventions, innovations, replays vidéo. Harvey, Jimini AI, Doctrine, CNB, Legal Data Space."
        : "Complete recap of TransfoDroit 2025: legal AI conferences, speakers, innovations, video replays. Harvey, Jimini AI, Doctrine, CNB, Legal Data Space."}
      seoKeywords="TransfoDroit, Transfo Droit, IA juridique, IA droit, IA avocat, salon IA juridique, transformations du droit 2025, legal tech, legaltech, congrès droit IA, Legal Data Space, agents IA juridique"
      canonicalUrl={language === 'fr'
        ? "https://gaius-tech.com/articles/transfo-droit-2025-recapitulatif-salon-ia-juridique"
        : "https://gaius-tech.com/articles/transfo-droit-2025-legal-ai-congress-recap"}
      alternateUrlFr="https://gaius-tech.com/articles/transfo-droit-2025-recapitulatif-salon-ia-juridique"
      alternateUrlEn="https://gaius-tech.com/articles/transfo-droit-2025-legal-ai-congress-recap"
      category={language === 'fr' ? 'Analyse Stratégique' : 'Strategic Analysis'}
      categoryColor="purple"
      title={language === 'fr'
        ? "TransfoDroit 2025 : récapitulatif complet du plus grand salon de l'IA juridique en Europe"
        : "TransfoDroit 2025: Complete Recap of Europe's Largest Legal AI Congress"}
      subtitle={language === 'fr'
        ? "4 800 participants, 80 exposants, 120+ conférences : retour détaillé et sourcé sur les deux journées du salon des Transformations du Droit."
        : "4,800 attendees, 80 exhibitors, 120+ conferences: detailed recap of the Transformations du Droit congress."}
      date={language === 'fr' ? '2 décembre 2025' : 'December 2, 2025'}
      dateISO="2025-12-02"
      readTime={language === 'fr' ? '22 min' : '22 min'}
      content={content}
      structuredData={structuredData}
    />
  );
};
